import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import Grid from '@mui/material/Grid';
import FormHelperText from '@mui/material/FormHelperText';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';

import TextFieldBase from '@/components/mui/Form/TextField/TextFieldBase';
import {
  INVALID_FILE_SIZE,
  INVALID_FILE_TYPE,
  MAX_FILE_SIZE_MB,
} from '@/pages/companies/components/CreateCompany/schema';

import {
  InputWrapper,
  PlusIconWrapper,
  StyledImage,
  UploadLogoIconButton,
} from '@/pages/companies/components/CreateCompany/components/UploadImg/sheet';

interface UploadImgProps {
  name: string;
  accept: string;
  file: File | null;
  onChange: (file: File | null) => void;
  // A code from the field's schema, or undefined while the file is acceptable.
  errorCode?: string;
}

const UploadImg: React.FC<UploadImgProps> = ({ name, accept, file, onChange, errorCode }) => {
  const { formatMessage } = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDamagedImage, setIsDamagedImage] = useState(false);

  // An object URL outlives the element that used it until it is revoked, and a
  // fresh one is minted for every picked file, so the previous one is released
  // when it is replaced and when the field goes away.
  const src = useMemo(() => (file ? window.URL.createObjectURL(file) : null), [file]);
  useEffect(
    () => () => {
      if (src) window.URL.revokeObjectURL(src);
    },
    [src],
  );

  const onChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const picked = event.target.files?.[0];
    if (!picked) return;
    setIsDamagedImage(false);
    onChange(picked);
  };

  const errorMessage = () => {
    if (isDamagedImage || errorCode === INVALID_FILE_TYPE) {
      return formatMessage({
        id: 'form.company.create.logo.invalid_file_type',
        defaultMessage: 'Invalid or unsupported file type. Accepted formats: PNG, JPG, JPEG, BMP',
      });
    }
    if (errorCode === INVALID_FILE_SIZE) {
      return formatMessage(
        {
          id: 'form.company.create.logo.invalid_file_size',
          defaultMessage: 'The file size exceeds the {maxSize}MB maximum limit.',
        },
        { maxSize: MAX_FILE_SIZE_MB },
      );
    }
    return errorCode ?? null;
  };

  const message = errorMessage();

  return (
    <>
      <TextFieldBase
        inputRef={fileInputRef}
        id="fileInput"
        type="file"
        name={name}
        slotProps={{ htmlInput: { accept } }}
        onChange={onChangeInput}
        sx={{ display: 'none' }}
      />
      <InputWrapper>
        <label htmlFor="fileInput">
          <Grid container alignItems="center" justifyContent="center">
            {src && !errorCode ? (
              <StyledImage
                src={src}
                alt="logo"
                onError={() => {
                  onChange(null);
                  setIsDamagedImage(true);
                }}
              />
            ) : (
              <UploadLogoIconButton
                aria-label={formatMessage({
                  id: 'form.company.create.logo.label',
                  defaultMessage: 'Company logo',
                })}
                onClick={() => fileInputRef.current?.click()}
              >
                <BusinessIcon sx={{ width: 40, height: 40 }} htmlColor="#66686A" />
                <PlusIconWrapper>
                  <AddIcon />
                </PlusIconWrapper>
              </UploadLogoIconButton>
            )}
          </Grid>
        </label>
        {message && <FormHelperText error>{message}</FormHelperText>}
      </InputWrapper>
    </>
  );
};

export default UploadImg;
